/**
 * The channels the signed-in account can access, and which one is active.
 *
 * Backed by GET /channels (own channel + any the account is a mod on). The
 * active selection persists in localStorage and defaults to the account's own
 * channel. The active channel's role gates the UI (mods can't toggle chat
 * ingestion or manage mods).
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Channel, type ChannelRole } from "../lib/api";

const KEY = "quelby.channelId";

export interface ChannelsState {
  channels: Channel[];
  channelId: string;
  setChannelId: (id: string) => void;
  /** The caller's role on the active channel, or null while loading/none. */
  role: ChannelRole | null;
  loading: boolean;
  /** Set when the channels list failed to load (so the UI can show it, not spin). */
  error: string | null;
}

export function useChannels(): ChannelsState {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelId, setChannelIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setChannelId = useCallback((id: string) => {
    setChannelIdState(id);
    try {
      localStorage.setItem(KEY, id);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    api
      .channels()
      .then(({ channels }) => {
        setError(null);
        setChannels(channels);
        setChannelIdState((prev) => {
          if (prev && channels.some((c) => c.channelId === prev)) return prev;
          const fallback = channels.find((c) => c.isOwn) ?? channels[0];
          return fallback?.channelId ?? "";
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  const role = channels.find((c) => c.channelId === channelId)?.role ?? null;
  return { channels, channelId, setChannelId, role, loading, error };
}
